// @ts-strict-ignore
import type { FetchRequestError } from "@lib/errors";
import type { RemoveFirstArgument } from "@lib/typeHelpers";

import type { AllRouteEndpoints } from "../routeTypes";
import type {
  GetInfiniteFetcherFunctionWithShadowError,
  InternalInfiniteGetFetcherFunctionWithShadowError,
} from "../types";
import type { CacheKeyParam, EndpointQueryKey, RootCacheKey } from "./types";

/**
 * Adapter from react-query fetcher function for infinite scroll queries.
 *
 * This takes the fetcher function and passes in the query key, minus the root cache key
 * to the function. It also passes the infinite scroll page parameter as the last parameter
 */
export const reactQueryInfiniteFetcherAdapter = <
  CacheKey extends AllRouteEndpoints,
  QueryKey extends readonly [rootKey: RootCacheKey<CacheKey>, ...originalKey: CacheKeyParam[]],
  Args extends RemoveFirstArgument<QueryKey>,
  Result,
  TPageParam = any,
  Error extends FetchRequestError = FetchRequestError,
>(
  _cacheKey: CacheKey,
  fetcherFunction: InternalInfiniteGetFetcherFunctionWithShadowError<
    Error,
    CacheKey,
    QueryKey,
    Args,
    Result,
    TPageParam
  >
): GetInfiniteFetcherFunctionWithShadowError<Error, CacheKey, QueryKey, Args, Result, TPageParam> => {
  return ({ queryKey: [, args], signal, pageParam }: EndpointQueryKey<CacheKey, [Args], TPageParam>) => {
    return fetcherFunction(signal, args, pageParam);
  };
};
